import {
  Component, inject, signal, OnInit, OnDestroy, ViewChild,
  ElementRef, AfterViewChecked, effect,
} from '@angular/core';
import { NgFor, NgIf, NgClass, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChatService, ChatMessage } from './chat.service';
import { Store } from '@ngrx/store';
import { selectAuthUser } from '@store/auth/auth.selectors';
import { firstValueFrom } from 'rxjs';
import { environment } from '@env/environment';
import { BRANDING } from '@core/branding/branding';

interface Toast {
  id: number;
  msgId: number;
  senderEmail: string;
  text: string;
  roomId: number;
}

interface LoginAlert {
  roomCount: number;
}

@Component({
  selector: 'erp-chat-widget',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, FormsModule, DatePipe],
  styles: [`
    @keyframes bounce-in {
      0%   { transform: scale(0.8); opacity: 0; }
      60%  { transform: scale(1.1); }
      100% { transform: scale(1);   opacity: 1; }
    }
    @keyframes slide-up {
      from { transform: translateY(12px); opacity: 0; }
      to   { transform: translateY(0);    opacity: 1; }
    }
    .panel-enter  { animation: bounce-in 0.22s ease-out; }
    .toast-enter  { animation: slide-up 0.2s ease-out; }
  `],
  template: `
    <!-- Botón flotante -->
    <button
      (click)="toggle()"
      [class.animate-bounce]="chatService.totalUnread() > 0 && !open()"
      class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full
             transition-all duration-200 hover:scale-110 active:scale-95"
      [ngClass]="b.chat.btnClasses"
      title="Chat interno"
    >
      <span
        *ngIf="chatService.totalUnread() > 0 && !open()"
        class="absolute inset-0 rounded-full animate-ping opacity-60"
        [ngClass]="b.chat.pingBg"
      ></span>
      <svg *ngIf="!open()" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" [attr.stroke]="b.chat.btnIconStroke" class="relative h-7 w-7">
        <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
      </svg>
      <svg *ngIf="open()" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" [attr.stroke]="b.chat.btnIconStroke" class="relative h-6 w-6">
        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
      </svg>
      <span
        *ngIf="chatService.totalUnread() > 0 && !open()"
        class="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full
               bg-red-500 px-1 text-[10px] font-black text-white ring-2 ring-white"
      >{{ chatService.totalUnread() }}</span>
    </button>

    <!-- Alerta de mensajes sin leer al iniciar sesión -->
    <div
      *ngIf="loginAlert() && !open()"
      class="toast-enter fixed bottom-24 right-6 z-50 flex w-80 cursor-pointer items-start gap-3
             rounded-2xl bg-white p-4 shadow-2xl pointer-events-auto"
      [ngClass]="b.chat.alertRing"
      (click)="openFromLoginAlert()"
    >
      <div class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
           [ngClass]="b.chat.avatarClasses">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
        </svg>
      </div>
      <div class="flex-1">
        <p class="text-sm font-bold text-slate-800">Mensajes sin leer</p>
        <p class="text-xs text-slate-500">
          Tienes conversaciones con mensajes nuevos. Haz clic para verlos.
        </p>
      </div>
      <button
        (click)="$event.stopPropagation(); loginAlert.set(null)"
        class="shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
        title="Cerrar"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="h-4 w-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Toasts -->
    <div class="fixed bottom-24 right-6 z-50 flex flex-col gap-2 pointer-events-none">
      <div
        *ngFor="let toast of toasts()"
        (click)="openFromToast(toast)"
        class="toast-enter pointer-events-auto flex w-72 cursor-pointer items-start gap-3 rounded-2xl
               bg-white p-3 shadow-xl ring-1 ring-slate-200 transition-all"
        [ngClass]="b.chat.toastRingHover"
      >
        <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold"
             [ngClass]="b.chat.avatarClasses">
          {{ toast.senderEmail.charAt(0).toUpperCase() }}
        </div>
        <div class="min-w-0 flex-1">
          <p class="text-xs font-bold text-slate-800 truncate">{{ toast.senderEmail }}</p>
          <p class="text-xs text-slate-500 truncate">{{ toast.text }}</p>
        </div>
        <span class="mt-0.5 text-[9px] font-semibold shrink-0" [ngClass]="b.chat.accentText">NUEVO</span>
      </div>
    </div>

    <!-- Panel del chat -->
    <div
      *ngIf="open()"
      class="panel-enter fixed bottom-24 right-6 z-50 flex h-[540px] w-80 flex-col
             rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3" [ngClass]="b.chat.headerClasses">
        <div class="flex items-center gap-2">
          <button
            *ngIf="chatService.activeRoomId() !== null"
            (click)="chatService.closeRoom()"
            class="mr-1 rounded-lg p-1 transition-colors"
            [ngClass]="b.chat.headerControlsClass"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2.5" stroke="currentColor" class="h-4 w-4">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
          </button>
          <div class="flex flex-col leading-tight">
            <span class="text-sm font-bold" [ngClass]="b.chat.headerTitleClass">
              {{ chatService.activeRoomId() !== null ? activeRoomName() : 'Chat interno' }}
            </span>
            <span
              *ngIf="chatService.activeRoomId() !== null"
              class="text-[10px] font-medium"
              [ngClass]="isActiveRoomOnline() ? b.chat.headerOnlineClass : b.chat.headerOfflineClass"
            >{{ isActiveRoomOnline() ? '● En línea' : '○ Desconectado' }}</span>
          </div>
          <span
            [class.bg-green-500]="chatService.connected()"
            [class.bg-red-500]="!chatService.connected()"
            class="h-2 w-2 rounded-full ring-2"
            [ngClass]="b.chat.headerConnDotRing"
            [title]="chatService.connected() ? 'Conectado' : (chatService.connectionError() ?? 'Desconectado')"
          ></span>
        </div>
        <button
          (click)="showNewChat.set(!showNewChat())"
          class="rounded-lg p-1.5 transition-colors"
          [ngClass]="b.chat.headerControlsClass"
          title="Nuevo chat"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5">
            <path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125" />
          </svg>
        </button>
      </div>

      <!-- Selector nuevo chat -->
      <div *ngIf="showNewChat()" class="flex flex-col border-b border-slate-100 bg-slate-50 px-3 py-2">
        <p class="mb-2 text-xs font-semibold text-slate-500">Iniciar chat con:</p>
        <input
          [(ngModel)]="userSearch"
          placeholder="Buscar usuario..."
          class="mb-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs outline-none"
          [ngClass]="b.chat.inputFocusClasses"
        />
        <div class="max-h-32 overflow-y-auto space-y-1">
          <button
            *ngFor="let u of filteredUsers()"
            (click)="startDirect(u.idUser)"
            class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs text-slate-700 transition-colors"
            [ngClass]="b.chat.userHoverClasses"
          >
            <span class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold"
                  [ngClass]="b.chat.userAvatarClasses">
              {{ u.email.charAt(0).toUpperCase() }}
            </span>
            <span class="truncate">{{ u.email }}</span>
          </button>
          <p *ngIf="filteredUsers().length === 0" class="py-2 text-center text-xs text-slate-400">Sin resultados</p>
        </div>
      </div>

      <!-- Lista de salas -->
      <div *ngIf="chatService.activeRoomId() === null && !showNewChat()" class="flex-1 overflow-y-auto">
        <div *ngIf="chatService.rooms().length === 0" class="flex h-full flex-col items-center justify-center gap-2 text-slate-400 py-8">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1" stroke="currentColor" class="h-10 w-10 opacity-30">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 9.75a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375m-13.5 3.01c0 1.6 1.123 2.994 2.707 3.227 1.087.16 2.185.283 3.293.369V21l4.184-4.183a1.14 1.14 0 0 1 .778-.332 48.294 48.294 0 0 0 5.83-.498c1.585-.233 2.708-1.626 2.708-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0 0 12 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018Z" />
          </svg>
          <p class="text-sm">Sin conversaciones</p>
          <button
            (click)="showNewChat.set(true)"
            class="rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors"
            [ngClass]="b.chat.startChatBtnClasses"
          >Iniciar chat</button>
        </div>

        <button
          *ngFor="let room of chatService.rooms()"
          (click)="openRoom(room.idChatRoom)"
          class="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50"
          [ngClass]="chatService.unreadCount()[room.idChatRoom] > 0 ? b.chat.unreadRoomBg : ''"
        >
          <div class="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-bold shadow-sm"
               [ngClass]="b.chat.avatarClasses">
            {{ room.displayName.charAt(0).toUpperCase() }}
            <span
              class="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-white"
              [class.bg-green-400]="isOnline(room)"
              [class.bg-slate-300]="!isOnline(room)"
              [title]="isOnline(room) ? 'En línea' : 'Desconectado'"
            ></span>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-1">
              <span
                class="truncate text-xs font-semibold text-slate-800"
                [ngClass]="chatService.unreadCount()[room.idChatRoom] > 0 ? b.chat.unreadNameText : ''"
              >{{ room.displayName }}</span>
              <span
                *ngIf="chatService.unreadCount()[room.idChatRoom] > 0"
                class="flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[9px] font-black"
                [ngClass]="b.chat.badgeClasses"
              >{{ chatService.unreadCount()[room.idChatRoom] }}</span>
            </div>
            <p
              class="truncate text-xs text-slate-400"
              [class.font-semibold]="chatService.unreadCount()[room.idChatRoom] > 0"
              [ngClass]="chatService.unreadCount()[room.idChatRoom] > 0 ? b.chat.accentTextStrong : 'text-slate-400'"
            >{{ room.lastMessage ?? 'Sin mensajes' }}</p>
          </div>
        </button>
      </div>

      <!-- Vista de mensajes -->
      <ng-container *ngIf="chatService.activeRoomId() !== null">
        <div #scrollArea class="flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-slate-50">
          <div *ngIf="chatService.activeMessages().length === 0" class="flex h-full items-center justify-center py-10">
            <p class="text-center text-xs text-slate-400">Sin mensajes aún.<br>¡Sé el primero en escribir!</p>
          </div>
          <div
            *ngFor="let msg of chatService.activeMessages()"
            [class.justify-end]="msg.idSender === myUserId()"
            class="flex"
          >
            <div
              class="max-w-[78%] rounded-2xl px-3 py-2"
              [ngClass]="msg.idSender === myUserId()
                ? b.chat.ownBubbleClasses
                : 'bg-white text-slate-800 rounded-bl-sm shadow-sm'"
            >
              <p *ngIf="msg.idSender !== myUserId()" class="mb-1 text-[10px] font-bold opacity-50">{{ msg.senderEmail }}</p>

              <!-- Adjunto de imagen -->
              <ng-container *ngIf="msg.fileUrl && isImage(msg.mimeType)">
                <a [href]="fileBaseUrl + msg.fileUrl" target="_blank" rel="noopener">
                  <img
                    [src]="fileBaseUrl + msg.fileUrl"
                    [alt]="msg.fileName ?? 'imagen'"
                    class="mb-1 max-w-full rounded-lg object-cover"
                    style="max-height:160px"
                  />
                </a>
              </ng-container>

              <!-- Adjunto de otro tipo -->
              <ng-container *ngIf="msg.fileUrl && !isImage(msg.mimeType)">
                <a
                  [href]="fileBaseUrl + msg.fileUrl"
                  target="_blank"
                  rel="noopener"
                  class="mb-1 flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors"
                  [class.bg-white]="msg.idSender === myUserId()"
                  [class.bg-opacity-20]="msg.idSender === myUserId()"
                  [class.bg-slate-100]="msg.idSender !== myUserId()"
                  [class.text-stone-800]="msg.idSender === myUserId()"
                  [ngClass]="msg.idSender !== myUserId() ? b.chat.accentTextStrong : ''"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-5 w-5 shrink-0 opacity-70">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
                  </svg>
                  <div class="min-w-0 flex-1">
                    <p class="truncate text-xs font-semibold">{{ msg.fileName }}</p>
                    <p class="text-[10px] opacity-60">{{ formatSize(msg.fileSize) }}</p>
                  </div>
                </a>
              </ng-container>

              <p *ngIf="!msg.fileUrl" class="text-xs leading-relaxed">{{ msg.text }}</p>
              <p class="mt-1 text-right text-[9px] opacity-50">{{ msg.createdAt | date:'HH:mm' }}</p>
            </div>
          </div>
        </div>

        <!-- Input de mensaje + botón de archivo -->
        <div class="border-t border-slate-100 bg-white px-3 py-2">
          <div *ngIf="chatService.uploading()"
               class="mb-2 flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs"
               [ngClass]="b.chat.uploadingClasses">
            <svg class="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Subiendo archivo...
          </div>
          <form (ngSubmit)="send()" class="flex gap-2">
            <input
              #fileInput
              type="file"
              class="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.txt,.csv"
              (change)="onFileSelected($event)"
            />
            <button
              type="button"
              (click)="fileInput.click()"
              [disabled]="chatService.uploading()"
              class="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-slate-200
                     bg-slate-50 text-slate-400 disabled:opacity-40 transition-all"
              [ngClass]="b.chat.attachHoverClasses"
              title="Adjuntar archivo"
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="h-4 w-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="m18.375 12.739-7.693 7.693a4.5 4.5 0 0 1-6.364-6.364l10.94-10.94A3 3 0 1 1 19.5 7.372L8.552 18.32m.009-.01-.01.01m5.699-9.941-7.81 7.81a1.5 1.5 0 0 0 2.112 2.13" />
              </svg>
            </button>
            <input
              [(ngModel)]="messageText"
              name="msg"
              placeholder="Escribe un mensaje..."
              autocomplete="off"
              (keydown.enter)="$event.preventDefault(); send()"
              class="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs outline-none transition-all"
              [ngClass]="b.chat.msgInputFocusClasses"
            />
            <button
              type="submit"
              [disabled]="!messageText.trim()"
              class="rounded-xl px-3 py-2 shadow-sm disabled:opacity-40 hover:shadow-md hover:scale-105
                     active:scale-95 transition-all"
              [ngClass]="b.chat.sendBtnBg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-4 w-4">
                <path d="M3.478 2.405a.75.75 0 0 0-.926.94l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.405Z" />
              </svg>
            </button>
          </form>
        </div>
      </ng-container>
    </div>
  `,
})
export class ChatWidgetComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly chatService = inject(ChatService);
  private store = inject(Store);

  protected readonly b = BRANDING;

  open = signal(false);
  showNewChat = signal(false);
  messageText = '';
  userSearch = '';
  myUserId = signal<number | null>(null);
  toasts = signal<Toast[]>([]);
  loginAlert = signal<LoginAlert | null>(null);

  @ViewChild('scrollArea') scrollArea?: ElementRef<HTMLDivElement>;
  private shouldScroll = false;
  private toastCounter = 0;
  private originalTitle = document.title;
  private audioCtx: AudioContext | null = null;

  readonly fileBaseUrl = environment.apiUrl.replace(/\/api$/, '');

  constructor() {
    effect(() => {
      const n = this.chatService.totalUnread();
      document.title = n > 0 ? `(${n}) ${this.originalTitle}` : this.originalTitle;
    });

    effect(() => {
      this.chatService.panelOpen.set(this.open());
    });

    effect(() => {
      const notif = this.chatService.lastIncomingMessage();
      const myId  = this.myUserId();
      if (!notif || !myId) return;
      if (notif.message.idSender === myId) return;
      if (this.open()) return;
      this.showToast(notif.message, notif.roomId);
    });
  }

  async ngOnInit(): Promise<void> {
    const user = await firstValueFrom(this.store.select(selectAuthUser));
    this.myUserId.set((user as any)?.idUser ?? (user as any)?.id ?? null);
    await this.chatService.connect();
    await this.chatService.loadRooms();
    await this.chatService.loadUsers();

    if (this.chatService.hasLoginUnread()) {
      const roomCount = Object.values(this.chatService.unreadCount())
        .filter(n => n > 0).length;
      this.loginAlert.set({ roomCount });
      this.playNotificationSound();
      setTimeout(() => this.loginAlert.set(null), 10000);
    }
  }

  ngOnDestroy(): void {
    this.chatService.disconnect();
    document.title = this.originalTitle;
  }

  ngAfterViewChecked(): void {
    if (this.shouldScroll && this.scrollArea) {
      const el = this.scrollArea.nativeElement;
      el.scrollTop = el.scrollHeight;
      this.shouldScroll = false;
    }
  }

  async toggle(): Promise<void> {
    this.open.update(v => !v);
    if (this.open()) {
      await this.chatService.loadRooms();
    }
  }

  async openRoom(roomId: number): Promise<void> {
    this.showNewChat.set(false);
    await this.chatService.openRoom(roomId);
    this.shouldScroll = true;
  }

  async startDirect(targetUserId: number): Promise<void> {
    this.showNewChat.set(false);
    await this.chatService.openDirectChat(targetUserId);
    this.shouldScroll = true;
  }

  send(): void {
    if (!this.messageText.trim()) return;
    this.chatService.sendMessage(this.messageText);
    this.messageText = '';
    this.shouldScroll = true;
  }

  async onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const roomId = this.chatService.activeRoomId();
    if (!roomId) return;

    input.value = '';
    try {
      await this.chatService.uploadFile(roomId, file);
      this.shouldScroll = true;
    } catch {
      // El error ya fue logueado en el servicio
    }
  }

  async openFromLoginAlert(): Promise<void> {
    this.loginAlert.set(null);
    this.open.set(true);
    await this.chatService.loadRooms();
  }

  async openFromToast(toast: Toast): Promise<void> {
    this.open.set(true);
    await this.openRoom(toast.roomId);
    this.dismissToast(toast.id);
  }

  activeRoomName(): string {
    const id = this.chatService.activeRoomId();
    return this.chatService.rooms().find(r => r.idChatRoom === id)?.displayName ?? 'Chat';
  }

  filteredUsers() {
    const q = this.userSearch.toLowerCase();
    return this.chatService.users().filter(u => u.email.toLowerCase().includes(q));
  }

  isOnline(room: { otherUserId: number | null }): boolean {
    if (!room.otherUserId) return false;
    return this.chatService.onlineUserIds().has(room.otherUserId);
  }

  isActiveRoomOnline(): boolean {
    const id = this.chatService.activeRoomId();
    const room = this.chatService.rooms().find(r => r.idChatRoom === id);
    return room ? this.isOnline(room) : false;
  }

  isImage(mimeType: string | null): boolean {
    return !!mimeType && mimeType.startsWith('image/');
  }

  formatSize(bytes: number | null): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  private shownMsgIds = new Set<number>();

  private showToast(msg: ChatMessage, roomId: number): void {
    if (this.shownMsgIds.has(msg.idChatMessage)) return;
    this.shownMsgIds.add(msg.idChatMessage);

    const id = ++this.toastCounter;
    const text = msg.fileUrl ? `📎 ${msg.fileName ?? 'Archivo'}` : msg.text;
    this.toasts.update(ts => [...ts, { id, msgId: msg.idChatMessage, senderEmail: msg.senderEmail, text, roomId }]);
    this.playNotificationSound();
    setTimeout(() => this.dismissToast(id), 5000);
  }

  private dismissToast(id: number): void {
    this.toasts.update(ts => ts.filter(t => t.id !== id));
  }

  private playNotificationSound(): void {
    try {
      if (!this.audioCtx) {
        this.audioCtx = new AudioContext();
      }
      const ctx = this.audioCtx;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.3);
    } catch {
      // Silently ignore
    }
  }
}
